import { isMobile, browserName, browserVersion } from "react-device-detect";
import { honeybadger } from "../../settings/IntegrationConstants";
import axios from "axios";

/**
 * Injects a script onto the page to get a variable and adds the variables value to a data attribute
 * NOTE - This method may fail due to strict CSP restrictions on a site.
 */
export const runScriptInPageContext = (
  elementId: string,
  scriptString: string
): void => {
  let previousEl = document.getElementById(elementId);
  if (previousEl) previousEl.parentElement?.removeChild(previousEl);
  let script = document.createElement("script");
  script.type = "text/javascript";
  script.charset = "utf-8";
  script.id = elementId;
  script.classList.add("gopher-script");
  script.defer = true;
  script.text = `(function () {
                      let script = document.getElementById('${elementId}');
                      ${scriptString}
                  })();`;
  document.head.appendChild(script);
};

/**
 * Get url parameter
 */
export const getUrlParameter = (
  name: string,
  str?: string
): string | undefined => {
  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  let regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
  let results = regex.exec(str ? str : window.location.search);
  return results === null
    ? undefined
    : decodeURIComponent(results[1].replace(/\+/g, " "));
};

/**
 * Set of methods for querySelectorWithFilters and querySelectorAllWithFilters
 */
type Filter = {
  type: "visible";
};

const switchFilters = (el: HTMLElement, filter: Filter): boolean => {
  if (filter.type === "visible") {
    return el.offsetHeight !== 0 || el.offsetWidth !== 0;
  }
  return true;
};

const filterQuerySelector = (
  selector: string,
  filters: Filter[]
): Element[] | undefined => {
  const els = Array.from(document.querySelectorAll(selector)).filter((el) => {
    const passesAllFilters = filters.reduce(
      (acc, cur) => acc && switchFilters(el as HTMLElement, cur),
      true
    );
    return passesAllFilters ? el : null;
  });
  return els;
};

export const querySelectorWithFilters = (
  selector: string,
  filters: Filter[]
): Element | undefined => {
  const els = filterQuerySelector(selector, filters);
  if (els) {
    return els[0];
  }
};

export const querySelectorAllWithFilters = (
  selector: string,
  filters: Filter[]
): Element[] | undefined => {
  return filterQuerySelector(selector, filters);
};

/**
 * Takes a string with a price in it and returns the float of the FIRST match. It looks for the first match of a
 * currency symbol followed by 0 or more spaces then numbers and . or , or \u00a0 (a NO-BREAK SPACE space) Then it handles the case where the number is
 * comma or decimal delimited by replacing anything not a number , or . Then making sure the decimal is delimited by
 * a . And finally removing any , or . that is not at the end of the number (i.e. anything separating the thousands place).
 */
export const parsePrice = (price: string): number => {
  if (typeof price === "number") return price;
  price = price.replace(/[*’]|Rs\.|rs\.|Rs|\u0020|\u00A0/g, "");
  const currency_symbol =
    "[\\x24\xA2-\xA5\u058F\u060B\u09F2\u09F3\u09FB\u0AF1\u0BF9\u0E3F\u17DB\u20A0-\u20BE\uA838\uFDFC\uFE69\uFF04\uFFE0\uFFE1\uFFE5\uFFE6]*";
  const currency_code = "[A-Z]{0,3}";
  const regex = new RegExp(currency_symbol + currency_code + "[ ]*[\\d,.‘-\\s]+");
  const match = regex.exec(price.trim());
  if (match) {
    return parseFloat(
      match[0]
        .replace(/[^,.0-9\-]/g, "")
        .replace(/[,.](?=\d{1,2}$)/g, ".")
        .replace(/[,.](?!\d{1,2}$)/g, "")
    );
  }

  if(price)
    throw new Error(`No match for price string: ${price}`);
};

// For input that utilizes react components, this is needed to fill out
export const setNativeValue = (element: HTMLInputElement, value: string) => {
  let lastValue = element.value;
  element.value = value;
  // @ts-ignore
  let event = new Event("input", { target: element, bubbles: true });
  // React 15
  (event as any).simulated = true;
  // React 16
  // @ts-ignore
  let tracker = element._valueTracker;
  if (tracker) {
    tracker.setValue(lastValue);
  }
  element.dispatchEvent(event);
};

export const getCurrencySymbol = (currency: string): string => {
  try {
    const a = 0;
    const symbol = a
      .toLocaleString("en", { style: "currency", currency: currency })
      .replace(/\d+([,.]\d+)?/g, "");

    return symbol; 
  } catch (error) {
    console.error('Error converting currency:', currency, error);
    return '';
  }
};

// This function is used to retrieve the index of the latest data value from window.dataLayer for Siteminder only
export const getLatestDataLayerValuesIndex = (): number => {
  let indexOfLatestValues = 0;
  
  for(let k = window.dataLayer.length - 1; k > 0; k--){
    if(window.dataLayer[k].hasOwnProperty("value") && window.dataLayer[k].value !== undefined){
      indexOfLatestValues = k;
      break;
    }
  }
  return indexOfLatestValues;
};

export const emailRegex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

export const getBrowserInfo = (): string => {
  return browserName + " " + browserVersion;
};

export const getDeviceInfo = (): boolean => {
  return isMobile;
};

// client side call to HB to report error
export const notifyHoneybadger = (error, functionName: string): void => {
  honeybadger.beforeNotify(function(notice) {
    console.log('%c Honeybadger Notify (utils.ts): ' + notice.stack, 'background: #fffcf2; color: #E65840');
    if (/integrations.theguestbook.com|gstbk.com|theguestbook.com/.test(notice.stack) == false) { return false; }
  });

  honeybadger.setContext({
    sessionStorage: sessionStorage,
  });

  honeybadger.notify(error, {
    name: `Error at ${functionName} - ${window.gb_data.bookingEngine} - BEI v2.0 [${window.gb_data.hotel.name}]`,
    message: `${error.name}: ${error.message}`, 
    tags: `bei_error,
          BEI_2,
          ${window.gb_data.bookingEngine},
          ${window.gb_data.widget.identifier}, 
          ${functionName}`,
  });
};


/**
 * For reporting HB errors, ping GB server to notify HB in the case of being blocked by CSP
 * @param error 
 * @param functionName 
 */
export const notifyHoneybadgerGB = (error, functionName: string): void => {
  let info = {
    context: JSON.stringify({
      "sessionStorage": sessionStorage,
      "stackTrace": error.stack ? error.stack : "none"
    }),
    name: `Error at ${functionName} - ${window.gb_data.bookingEngine} - BEI v2.0 [${window.gb_data.hotel.name}]`,
    message: `${error.name}: ${error.message}`, 
    tags: `bei_error,
          BEI_2,
          ${window.gb_data.bookingEngine},
          ${window.gb_data.widget.identifier}, 
          ${functionName}`,
    href: window.location.href,
  };

  axios.post(
    `https://theguestbook.com/notify_honeybadger`,
    info,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    }
  ).then((response) => {
    console.log("HB notify success");
  })
  .catch((error) => {
    console.log("HB notify error: ", error);
  });
};


/**
 * sets cookie in document
 * @param cname name of the cookie
 * @param cvalue the value of the cookie
 * @param exdays number of days until the cookie should expire
 */
export const setCookie = (cname, cvalue, exdays) => {
  const d = new Date();
  d.setTime(d.getTime() + (exdays*24*60*60*1000));
  let expires = "expires="+ d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
};

export const getCookie = (cname) => {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for(let i = 0; i <ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export const saveFullURL = () => {
  let url = window.location.href;
  if(url){
      setCookie("full_booking_url", url, 30);
  }
};

export const getDataLayerKeyForward = (key) => {
  for (let i = 0; i < window.dataLayer.length; i++) {
      let dataLayerElem = window.dataLayer[i];
      if (key in dataLayerElem) {
        return dataLayerElem[key];
      }
  }
  return null;
}

export const getDataLayerKeyReverse = (key) => {
  for (let i = (window.dataLayer.length - 1); i >= 0; i--) {
      let dataLayerElem = window.dataLayer[i];
      if (key in dataLayerElem) {
        return dataLayerElem[key];
      }
  }
  return null;
}

/* Returns true if rate plan code not eligible for rewards - returns false if rate plan code is eligible for rewards. */
export const isNotEligibleForRewards = (element, index, array) => {
  let hotelBlackListedRatePlanCodes = (window as any).gb_data.hotel.rateCodeExclusions.map(obj => obj.rate_plan_code.toLowerCase());
  let hotelWhiteListedRatePlanCodes = (window as any).gb_data.hotel.rateCodeInclusions;
  // Helper, check exclusion codes against var element
  const isBlackListedCode = (element, index, array) => {
    if(!element) return false;
    let sentCode = element.toLowerCase();
    let shouldExclude = hotelBlackListedRatePlanCodes.indexOf( sentCode ) != -1;
    let wildcards = hotelBlackListedRatePlanCodes.filter(function(x){return x.indexOf('*') > -1});
    if(shouldExclude) return true;

    for (let i=0; i < wildcards.length; i++) {
      let hotelAdminWildcard = wildcards[i];
      // Test for anything that ends with desired character(s) [*XX]
      let regexEnds = RegExp('^[*].*[^*]$');
      // Test for anything that starts with desired character(s) [XX*]
      let regexStarts = RegExp('^[^*].*[*]$'); 
      // Test for anything that contains desired character(s) [*XX*]
      let regexContains = RegExp('^[*].*[*]$')
      // Element to test against regex  - element stripped of *
      let hotelAdminConvertedWildcard = hotelAdminWildcard.replace(/[*]/g,'');
      if (regexEnds.test(hotelAdminWildcard)) {
        let trueRegexEndsTest = '^\\w*(' + hotelAdminConvertedWildcard + ')$';
        let regex = RegExp(trueRegexEndsTest);
        if (regex.test(sentCode)) {
          return true;
        }
      } else if (regexStarts.test(hotelAdminWildcard)) {
        let trueRegexStartsTest = '^(' + hotelAdminConvertedWildcard + ')\\w*$';
        let regex = RegExp(trueRegexStartsTest);
        if (regex.test(sentCode)) {
          return true;
        }
      } else if (regexContains.test(hotelAdminWildcard)) {
        let trueRegexContainsTest = '^\\w*(' + hotelAdminConvertedWildcard + ')\\w*$';
        let regex = RegExp(trueRegexContainsTest);
        if (regex.test(sentCode)) {
          return true;
        }
      }
    }
    return false;
  }
  // Helper, check inclusion codes against var element 
  //  takes precedent over blacklisted codes / exclusions
  const isWhiteListedCode = (element) => {    
    if (!element) return false;
    if (hotelWhiteListedRatePlanCodes.length) {
      for (let i=0; i < hotelWhiteListedRatePlanCodes.length; i++) {
        let whitelistedCode = hotelWhiteListedRatePlanCodes[i].toLowerCase();
        if (whitelistedCode == element.toLowerCase()) {
          return true; 
        }
      }
    }
    return false;
  }

  // If we have a list of rates to exclude
  if (hotelBlackListedRatePlanCodes.length) {
    let whiteListed = hotelWhiteListedRatePlanCodes.length && isWhiteListedCode(element);
    // Rate is not whitelisted, check it against blacklist in LAP
    if (!whiteListed){
      return isBlackListedCode(element, index, array);
    // Rate is whitelisted, do not exclude
    } else {
      return false;
    }
  }
  // if whitelist and no blacklist, return false if elem is in whitelist
  else if(hotelWhiteListedRatePlanCodes.length){
    return !isWhiteListedCode(element);
  }
  return false;
}
